import { Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { InputText, Checkbox, Autocomplete, Select, IconClose } from '@ui/src';
import { getPspPartnerType } from '../../hoc/bank-and-psp/helpers/get-enums-names';
import ItemFormBlock from './item-form-block';
import {
  PspPartnerLocation,
  PspPartnerType,
  ExportFileTypeEnum,
} from '../../hoc/bank-and-psp/types';
import PaymentTypesInput from './payment-types-input';

import cn from './item-form.module.css';
import { BankAndPspItemFormTypes } from './types';

const types = [
  {
    name: getPspPartnerType(PspPartnerType.CREDIT),
    value: PspPartnerType.CREDIT,
  },
  {
    name: getPspPartnerType(PspPartnerType.EMONEY),
    value: PspPartnerType.EMONEY,
  },
  {
    name: getPspPartnerType(PspPartnerType.PAYMENT),
    value: PspPartnerType.PAYMENT,
  },
  {
    name: getPspPartnerType(PspPartnerType.BLOCKCHAIN_CUSTODIAN_EXCHANGE),
    value: PspPartnerType.BLOCKCHAIN_CUSTODIAN_EXCHANGE,
  },
];

const locations = [
  {
    name: 'Domestic',
    value: PspPartnerLocation.DOMESTIC,
  },
  {
    name: 'Foreign',
    value: PspPartnerLocation.FOREIGN,
  },
];

const BankAndPspItemForm = ({
  control,
  errors,
  itemType,
  itemLocation,
  countries,
  isEditable,
  itemId,
  pspIntegration,
  sortCode,
  setSortCode,
  setValue,
  paymentTypes,
  casp,
  caspAvailable,
  caspId,
  abaAvailable,
  aba,
}: BankAndPspItemFormTypes) => {
  const [paymentsTypes, setPaymentTypes] = useState<string[]>([]);

  useEffect(() => {
    if (pspIntegration) {
      const paymentsTypesArray: string[] = [];

      pspIntegration.pspIntegrationPayments.forEach((item) => {
        item.paymentsTypes.forEach((paymentType) => {
          const currentPaymentType = paymentTypes?.find(
            (type) => type.paymentType === paymentType,
          );
          if (
            currentPaymentType &&
            !paymentsTypesArray.find(
              (type) => type === currentPaymentType.paymentType,
            )
          ) {
            paymentsTypesArray.push(paymentType);
          }
        });
      });

      setPaymentTypes(paymentsTypesArray);
    }
  }, [pspIntegration]);

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    let inputValue = sortCode;

    if (e.key !== 'Backspace') {
      const regex = /.{1,2}/g;

      inputValue = sortCode
        ?.replace(/[^0-9]/g, '')
        ?.match(regex)
        ?.join('-');
    }

    setSortCode(inputValue as string);
    setValue('sortCode', inputValue);
  };

  return (
    <div className={cn.itemForm}>
      <ItemFormBlock title="Type">
        <>
          {types.map((type) => (
            <div className={cn.itemFormRadioWrapper} key={type.value}>
              <Controller
                control={control}
                name="partnerType"
                defaultValue=""
                render={({ field: { onChange } }) => (
                  <Checkbox
                    name={`type-${type.value}`}
                    title={type.name}
                    onChange={() => onChange(type.value)}
                    type="radio"
                    checked={Boolean(itemType === type.value)}
                    disabled={Boolean(itemId && !isEditable)}
                    tabIndex={itemId && !isEditable ? -1 : 0}
                  />
                )}
              />
            </div>
          ))}
          {errors?.partnerType && (
            <div className={cn.error}>{errors?.partnerType.message}</div>
          )}
        </>
      </ItemFormBlock>
      <ItemFormBlock title="Location">
        <>
          {locations.map((location) => (
            <div className={cn.itemFormRadioWrapper} key={location.value}>
              <Controller
                control={control}
                name="location"
                defaultValue=""
                render={({ field: { onChange } }) => (
                  <Checkbox
                    name={`location-${location.value}`}
                    title={location.name}
                    onChange={() => onChange(location.value)}
                    type="radio"
                    checked={Boolean(itemLocation === location.value)}
                    disabled={Boolean(itemId && !isEditable)}
                    tabIndex={itemId && !isEditable ? -1 : 0}
                  />
                )}
              />
            </div>
          ))}
          {errors?.location && (
            <div className={cn.error}>{errors?.location.message}</div>
          )}
        </>
      </ItemFormBlock>
      <ItemFormBlock title="Export File Type">
        <Controller
          control={control}
          name="exportFileType"
          render={({ field: { onChange, name, value } }) => (
            <Select
              name={name}
              value={value as string}
              list={[
                {
                  name: '-',
                  value: ExportFileTypeEnum.NULL,
                },
                {
                  name: 'Harbour&Hills B2B Lite',
                  value: ExportFileTypeEnum.HARBOUR_HILLS_B2B_LITE,
                },
                {
                  name: 'Spitamen',
                  value: ExportFileTypeEnum.SPITAMEN,
                },
              ]}
              placeholder="Select export file type"
              error={errors?.exportFileType?.message}
              onChange={onChange}
              withClearButton
              fullWidth
            />
          )}
        />
      </ItemFormBlock>
      {itemId && pspIntegration?.id && (
        <ItemFormBlock title="Supported Payment Types & Currencies">
          <>
            <PaymentTypesInput
              control={control}
              isEditable={isEditable}
              itemId={itemId}
              paymentsTypes={paymentsTypes}
              paymentTypes={paymentTypes}
            />
            {errors?.paymentsTypes && (
              <div className={cn.error}>{errors?.paymentsTypes.message}</div>
            )}
          </>
        </ItemFormBlock>
      )}

      <div className={cn.itemFormBlockTitle}>Enter Details</div>
      <div className={cn.itemFormGroup}>
        <Controller
          control={control}
          name="companyName"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="companyName"
              placeholder="Enter Bank / PSP name"
              onChange={onChange}
              value={value}
              title="Bank / PSP name"
              error={errors?.companyName?.message}
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.itemFormGroup}>
        <Controller
          control={control}
          name="legalName"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="legalName"
              placeholder="Enter Legal name"
              onChange={onChange}
              value={value}
              title="Legal name"
              error={errors?.legalName?.message}
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.itemFormGroup}>
        <InputText
          name="sortCode"
          placeholder="Enter Sort code"
          onChange={(e) => {
            setSortCode(e.target.value);

            if (!e.target.value) {
              setSortCode('');
            }
          }}
          onKeyUp={onKeyUp}
          value={sortCode}
          title="Sort Code"
          error={errors?.sortCode?.message}
          maxLength={8}
          disabled={Boolean(itemId && !isEditable)}
          tabIndex={itemId && !isEditable ? -1 : 0}
        />
        <Controller
          control={control}
          name="sortCode"
          defaultValue=""
          render={({ field: { value } }) => (
            <input type="hidden" name="sortCode" value={value} />
          )}
        />
      </div>
      <div className={cn.itemFormGroup}>
        <Controller
          control={control}
          name="swift"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="swift"
              placeholder="Enter SWIFT / BIC"
              onChange={onChange}
              value={value}
              title="SWIFT / BIC"
              error={errors?.swift?.message}
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      {(abaAvailable || aba) && (
        <div className={cn.itemFormGroup}>
          <Controller
            control={control}
            name="aba"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <InputText
                name="aba"
                placeholder="Enter ABA"
                onChange={onChange}
                value={value}
                title="ABA"
                error={errors?.aba?.message}
                disabled={Boolean(itemId && !isEditable)}
                tabIndex={itemId && !isEditable ? -1 : 0}
              />
            )}
          />
        </div>
      )}

      <div
        className={`${cn.itemFormGroup} ${
          !caspAvailable && !caspId ? cn.itemFormGroupBigMargin : ''
        }`}
      >
        <Controller
          control={control}
          name="beneficiary"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="beneficiary"
              placeholder="Enter Beneficiary"
              onChange={onChange}
              value={value}
              title="Beneficiary"
              error={errors?.beneficiary?.message}
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      {(caspAvailable || caspId) && (
        <div className={`${cn.itemFormGroup} ${cn.itemFormGroupBigMargin}`}>
          <Controller
            control={control}
            name="caspId"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <div className={cn.itemFormGroupFlex}>
                <Select
                  name="caspId"
                  placeholder="Select CASP"
                  list={casp?.map((item) => ({
                    name: item.name,
                    value: item.id,
                  }))}
                  onChange={onChange}
                  value={value as string}
                  title="CASP"
                  error={errors?.caspId?.message}
                  className={cn.caspSelect}
                  disabled={
                    Boolean(itemId && !isEditable) ||
                    Boolean(!caspAvailable && caspId)
                  }
                  tabIndex={
                    (itemId && !isEditable) || Boolean(!caspAvailable && caspId)
                      ? -1
                      : 0
                  }
                  fullWidth
                />
                {value && (
                  <button
                    type="button"
                    className={cn.clearCasp}
                    onClick={() => onChange('')}
                    aria-label="close"
                  >
                    <IconClose />
                  </button>
                )}
              </div>
            )}
          />
        </div>
      )}

      <div className={cn.itemFormGroup}>
        <Controller
          control={control}
          name="countryId"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              list={countries}
              placeholder="Choose country"
              name="countryId"
              value={value}
              onChange={onChange}
              title="Country"
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
              className={
                errors?.countryId?.message ? cn.pspPartnerCountriesError : ''
              }
              error={errors?.countryId?.message}
              withClearButton
              fullWidth
            />
          )}
        />
      </div>
      <div className={cn.itemFormGroup}>
        <Controller
          control={control}
          name="address"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="address"
              placeholder="Enter Address"
              onChange={onChange}
              value={value}
              title="Address"
              error={errors?.address?.message}
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.itemFormGroup}>
        <Controller
          control={control}
          name="managerContacts"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="managerContacts"
              placeholder="Enter manager contacts"
              onChange={onChange}
              value={value}
              title="Name / contacts manager"
              error={errors?.managerContacts?.message}
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={`${cn.itemFormGroup} ${cn.itemFormGroupBigMargin}`}>
        <Controller
          control={control}
          name="onlineBankLink"
          render={({ field: { onChange, value } }) => (
            <InputText
              name="onlineBankLink"
              placeholder="Enter Internet bank link"
              onChange={onChange}
              value={value}
              title="Internet bank link"
              error={errors?.onlineBankLink?.message}
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.itemFormGroup}>
        <Controller
          control={control}
          name="intermediaryBankName"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="intermediaryBankName"
              placeholder="Enter Intermediary bank"
              onChange={onChange}
              value={value}
              title="Intermediary bank (optional)"
              error={errors?.intermediaryBankName?.message}
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.itemFormGroup}>
        <Controller
          control={control}
          name="intermediaryBankSwift"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="intermediaryBankSwift"
              placeholder="Enter Intermediary bank Swift"
              onChange={onChange}
              value={value}
              title="Intermediary bank Swift (optional)"
              error={errors?.intermediaryBankSwift?.message}
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={`${cn.itemFormGroup} ${cn.itemFormGroupBigMargin}`}>
        <Controller
          control={control}
          name="accountOfCorrespondentWithIntermediaryBank"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="accountOfCorrespondentWithIntermediaryBank"
              placeholder="Enter Account of Correspondent with Intermediary Bank"
              onChange={onChange}
              value={value}
              title="Account of Correspondent with Intermediary Bank (optional)"
              error={
                errors?.accountOfCorrespondentWithIntermediaryBank?.message
              }
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.itemFormGroup}>
        <Controller
          control={control}
          name="description"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="description"
              placeholder="Enter description"
              onChange={onChange}
              value={value}
              title="Description (optional)"
              error={errors?.description?.message}
              disabled={Boolean(itemId && !isEditable)}
              tabIndex={itemId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
    </div>
  );
};

export default BankAndPspItemForm;
